/** Executive ids are account-local; ordinary chat ids keep their existing identity. */
let session: string | null = null;
let generation = 0;
const disposers = new Set<() => void>();

export function executiveResourceKey(conversationId: string): string {
  return conversationId.startsWith('exec__')
    ? JSON.stringify(['executive', session, conversationId])
    : conversationId;
}

export function isExecutiveResourceKey(key: string): boolean {
  return key.startsWith('["executive",');
}

/** Invalidates even an A → B → A round trip, before any asynchronous cleanup. */
export function captureExecutiveSession(conversationId: string): () => boolean {
  const captured = generation;
  return () => !conversationId.startsWith('exec__') || captured === generation;
}

export function onExecutiveSessionDisposed(dispose: () => void): () => void {
  disposers.add(dispose);
  return () => { disposers.delete(dispose); };
}

/** Call before publishing a changed authenticated identity or tenant. */
export function setExecutiveSession(next: string | null): void {
  if (session === next) return;
  generation++;
  session = next;
  for (const dispose of disposers) dispose();
}

/** Registry shared by stores and readers; ordinary conversations retain their lifetime. */
export class ExecutiveResourceMap<T> extends Map<string, T> {
  constructor(dispose: (resource: T) => void) {
    super();
    onExecutiveSessionDisposed(() => {
      for (const [key, resource] of this) {
        if (!isExecutiveResourceKey(key)) continue;
        dispose(resource);
        this.delete(key);
      }
    });
  }
}


/** Both Executive queries share the authenticated operator/tenant cache scope. */
export function executiveQueryScope(
  user: {userId?: unknown; companyName?: unknown} | null | undefined,
  executiveTabEnabled: boolean,
  requested: boolean,
): {key: string; enabled: boolean} {
  const company = String(user?.companyName || '').trim();
  const userId = String(user?.userId || '').trim();
  return {
    key: JSON.stringify([userId, company, executiveTabEnabled]),
    enabled: requested && executiveTabEnabled && Boolean(userId && company),
  };
}
